import {
    FacebookLoginProvider,
    GoogleInitOptions,
    GoogleLoginProvider,
    GoogleSigninButtonModule,
    MicrosoftLoginProvider,
    SocialAuthService,
    SocialUser,
} from '@abacritt/angularx-social-login';
import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatBottomSheet, MatBottomSheetConfig } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { NavigationConfigService } from '@fuse/services/config.service';
import { NavigationConfig } from '@fuse/types';
import { OAuthSocialUser } from '@ms/core/auth/oauth-user.model';
import { PrincipalService } from '@ms/core/auth/principal.service';
import { DestroySubscribers } from '@ms/core/decorator/ngx-destroy-subscribers';
import { ServerInfo } from '@ms/core/model/server-info.model';
import { IUser } from '@ms/core/model/user.model';
import { EventManager } from '@ms/core/service/event-manager.service';
import { FlexModule } from '@ngbracket/ngx-layout/flex';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LayoutService } from 'app/layout/layout.service';
import { environment } from 'environments/environment';
import { NgxCaptchaModule } from 'ngx-captcha';
import { Subscription, timer, Unsubscribable } from 'rxjs';
import { MsAbstractBaseDialog } from '../base/base-dialog';
import { DialogService } from '../dialog/service/dialog.service';
import { LoginService } from './login.service';
import { SpinnerOverlayService } from './spinner/spinner-overlay/spinner-overlay.service';
import { SessionStoreService } from './state/session.store';

@Component({
    selector: 'login',
    templateUrl: './login-dialog.component.html',
    styleUrls: ['./login-dialog.component.scss'],
    imports: [
        FlexModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FontAwesomeModule,
        MatCheckboxModule,
        NgxCaptchaModule,
        MatButtonModule,
        TranslatePipe,
        MatDividerModule,
        GoogleSigninButtonModule,
        MatDialogModule,
        NgOptimizedImage,
    ],
})
@DestroySubscribers()
export class LoginDialogComponent extends MsAbstractBaseDialog implements OnInit, OnDestroy {
    subscriber: Unsubscribable;
    loginForm: FormGroup;
    authenticationError: boolean;
    hidePassword = true;
    useCaptcha = false;
    captchaSuccess = false;
    showVerifyCode = false;
    verifyCode = '';
    account: IUser;
    timeOutInfo = '';
    serverInfo: ServerInfo;
    loginStarted = signal(false);
    logo: string;
    logoWidth: string;
    readonly notification = viewChild<TemplateRef<any>>('notification');
    counter = 180;
    tick = 1000;
    countDown: Subscription;
    GoogleLoginProvider = GoogleLoginProvider;
    version: string;
    appUrl: string;
    constructor(
        private _NavigationConfigService: NavigationConfigService,
        private _formBuilder: FormBuilder,
        private loginService: LoginService,
        private _NavigationService: NavigationService,
        private _sessionQuery: SessionStoreService,
        private dialogService: DialogService,
        private principal: PrincipalService,
        private translate: TranslateService,
        private _progressService: FuseProgressBarService,
        private em: EventManager,
        private cdr: ChangeDetectorRef,
        private overlayService: SpinnerOverlayService,
        public layoutService: LayoutService,
        readonly bottomSheet: MatBottomSheet,
        private socialAuthService: SocialAuthService
    ) {
        super();
        if (environment.production) {
            //localStorage.clear();
            sessionStorage.clear();
            //this.cookieService.deleteAll();
        }
        this.fullScreenOnMobile.set(false);
        this.logo = environment.logo;
        this.logoWidth = `${environment.logoWidth}px`;
        this.version = environment.version;

        // Configure the layout
        this._NavigationConfigService.config = {
            layout: {
                navbar: {
                    hidden: true,
                },
                toolbar: {
                    hidden: false,
                },
                footer: {
                    hidden: true,
                },
                sidepanel: {
                    hidden: true,
                },
            },
        };
        // dev mode and routed to /login page then remove all localStorage infos
        // if (!environment.production) {
        // localStorage.clear(); // Clear all data from storage
        // }
        console.log('Login init ...');
        this.loginForm = this._formBuilder.group({
            username: [null, [Validators.required]],
            password: [null, Validators.required],
            rememberMe: [true],
            recaptcha: [false, Validators.required],
            verifyCode: [''],
            showVerifyCode: [false],
        });

        const rm = sessionStorage.getItem('rememberMe');
        if (rm && rm == 'true') {
            this.loginForm.patchValue({ rememberMe: rm });
        }
        this._NavigationService.register('main', []);

        if (this.principal.isAuthenticated) {
            this.loginService.logout();
        }

        this.subscriber = this.em.subscribe('show-login', () => {
            this.reset();
        });
        this.loginService.logoutStarted = false;
    }

    /**
     * On init
     */
    ngOnInit(): void {
        super.ngOnInit();
        this.loginStarted.set(false);
        this.useCaptcha = environment.useCaptcha || false;
        if (this.useCaptcha) {
            this.loginForm.patchValue({
                recaptcha: false,
            });
        } else {
            this.loginForm.patchValue({
                recaptcha: true,
            });
        }
        this.cdr.detectChanges();
        this.serverInfo = environment.serverInfo;
        if (this.serverInfo.notification) {
            const config: MatBottomSheetConfig = {
                hasBackdrop: false,
                closeOnNavigation: true,
            };
            this.bottomSheet.open(this.notification(), config);
        }
        this.subscriber = this.socialAuthService.authState.subscribe(socialUser => {
            if (socialUser) {
                this.loginSocialWithAccount(socialUser);
            }
        });
        this.loginForm.markAsUntouched();
        this.appUrl = environment.appUrl;
    }
    get canLogin() {
        return !(this.useCaptcha ? this.loginForm.valid && this.captchaSuccess == true : this.loginForm.valid);
    }

    get forgetPasswordUrl() {
        return environment.resetPasswordUrl;
    }

    onSubmit() {
        this.dialogService.closeToasters();
        if (this.loginForm.invalid) {
            return;
        }
        const rm = this.loginForm.get('rememberMe');
        if (rm) {
            sessionStorage.setItem('rememberMe', this.loginForm.get('rememberMe').value);
        } else {
            sessionStorage.removeItem('rememberMe');
        }
        console.log('RememberMe', sessionStorage.getItem('rememberMe'));
        this.loginStarted.set(true);
        this.overlayService.show();
        this.login(
            this.loginForm.get('username').value.trim(),
            this.loginForm.get('password').value.trim(),
            this.loginForm.get('rememberMe').value
        );
    }

    login(username: string, password: string, rememberMe: boolean) {
        this.loginService
            .login(
                {
                    username,
                    password,
                    rememberMe,
                },
                // Default login method
                (account: IUser) => {
                    //console.log('ACCOUNT', account);
                    this.account = account;
                    this.afterLogin();
                },
                // is_2fa_enabled login method
                () => {
                    this.countDown = timer(0, this.tick).subscribe(() => {
                        if (this.counter > 0) {
                            --this.counter;
                        }
                    });
                    this.showVerifyCode = true;
                    this.loginStarted.set(false);
                    console.log('verify code');
                    this.dialogService.info(this.translate.instant('LOGIN.CODE_SENDED'));
                    this.overlayService.hide();
                },
                // Change password
                () => {
                    this.loginStarted.set(false);
                    this.overlayService.hide();
                    this.em.broadcast({ name: 'change-password', content: {} });
                }
            )
            .then(() => {
                console.log('state', this._sessionQuery);
            })
            .catch(error => {
                setTimeout(() => {
                    this.overlayService.hide();
                });
                this.authenticationError = true;
                this.account = undefined;
                //GATEWAY_TIMEOUT
                if (error.status == 504) {
                    this.dialogService.error(this.translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT'));
                }
            })
            .finally(() => {
                this.loginStarted.set(false);
                this._progressService.hide();
            });
    }

    loginWithMicrosoft() {
        this.socialAuthService
            .signIn(MicrosoftLoginProvider.PROVIDER_ID)
            .then(socialUser => {
                console.log('MICROSOFT', socialUser);
                this.loginSocialWithAccount(socialUser);
            })
            .catch(() => {
                // this.dialogService.warning(this.translate.instant(this.translations.GENERAL.MESSAGE.WARNING.POPUP_BLOKER_ENABLED));
            });
    }
    loginWithFacebook(): void {
        const facebookLoginOptions = {
            scope: 'public_profile email',
        };
        this.socialAuthService
            .signIn(FacebookLoginProvider.PROVIDER_ID, facebookLoginOptions)
            .then(socialUser => {
                console.log('FACEBOOK', socialUser);
                this.loginSocialWithAccount(socialUser);
            })
            .catch(() => {
                // this.dialogService.warning(this.translate.instant(this.translations.GENERAL.MESSAGE.WARNING.POPUP_BLOKER_ENABLED));
            });
    }

    loginWithGoogle(): void {
        const googleLoginOptions: GoogleInitOptions = {
            scopes: 'profile email',
            //ux_mode: 'redirect',
        };
        this.socialAuthService
            .signIn(GoogleLoginProvider.PROVIDER_ID, googleLoginOptions)
            .then(socialUser => {
                console.log('GOOGLE', socialUser);
                this.loginSocialWithAccount(socialUser);
            })
            .catch(error => {
                console.log(error);
                // this.dialogService.warning(this.translate.instant(this.translations.GENERAL.MESSAGE.WARNING.POPUP_BLOKER_ENABLED));
            });
    }

    loginSocialWithAccount(socialUser: SocialUser) {
        this.loginStarted.set(true);
        this.overlayService.show();
        const token = new OAuthSocialUser();
        token.authToken = socialUser.authToken;
        token.authorizationCode = socialUser.authorizationCode;
        token.email = socialUser.email;
        token.firstName = socialUser.firstName;
        token.id = socialUser.id;
        token.idToken = socialUser.idToken;
        token.provider = socialUser.provider;
        this.loginService
            .loginViaSocial(
                token,
                // Default login method
                (account: IUser) => {
                    this.account = account;
                    this.afterLogin();
                },
                // is_2fa_enabled login method
                () => {
                    this.showVerifyCode = true;
                    this.loginStarted.set(false);
                    console.log('verify code');
                    this.dialogService.info(this.translate.instant('LOGIN.CODE_SENDED'));
                    this.overlayService.hide();
                },
                // Change password
                () => {
                    this.loginStarted.set(false);
                    this.overlayService.hide();
                    this.em.broadcast({ name: 'change-password', content: {} });
                }
            )
            .then(() => {
                console.log('state', this._sessionQuery);
            })
            .catch(error => {
                setTimeout(() => {
                    this.overlayService.hide();
                });
                this.authenticationError = true;
                this.account = undefined;
                //GATEWAY_TIMEOUT
                if (error.status == 504) {
                    this.dialogService.error(this.translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT'));
                }
            })
            .finally(() => {
                this.loginStarted.set(false);
                this._progressService.hide();
            });
    }

    afterLogin() {
        this.authenticationError = false;
        this._NavigationConfigService.config = NavigationConfig;
        this.loginService.afterLogin(this.account);
        this.hideOverlay();
    }

    hideOverlay() {
        console.log('After Login');

        if (this.principal.isAuthenticated) {
            super.close();
        }
    }

    onVerifyCodeSubmit() {
        this.loginStarted.set(true);
        this.overlayService.show();
        setTimeout(() => {
            this.loginService
                .verifyCode(this.loginForm.get('verifyCode').value, this.loginForm.get('username').value)
                .subscribe(
                    () => {
                        this.principal.identity().then(account => {
                            // After the login the language will be changed to
                            // the language selected by the user during his registration
                            this.account = account;
                            this.afterLogin();
                        });
                    },
                    error => {
                        this.account = undefined;
                        this.loginStarted.set(false);
                        this.overlayService.hide();
                        if (error.error?.title?.startsWith('Too many')) {
                            this.dialogService.error(this.translate.instant('LOGIN.TOO_MANY'));
                        } else if (
                            error.error?.title?.startsWith('Verification') ||
                            error.error?.title?.startsWith('Method')
                        ) {
                            this.dialogService.error(this.translate.instant('LOGIN.CODE_INVALID'));
                        } else {
                            this.dialogService.error(error.error.title);
                        }
                    }
                );
        }, 100);
    }

    reSendVerifyCode() {
        this.loginService.reSendVerifyCode(this.loginForm.get('username').value).subscribe(result => {
            console.log('Verification Code Sended !!!', result);
            this.dialogService.info(this.translate.instant('LOGIN.CODE_RE_SENDED'));
            this.counter = 180;
            // this.sessionTimeOutService.resetTimer(3 * 1000 * 60);
        });
    }

    handleSuccess(): void {
        this.captchaSuccess = true;
    }

    reset() {
        this.loginStarted.set(false);
        this.showVerifyCode = false;
        this.overlayService.hide();
    }

    timeFormat(value: number): string {
        const minutes: number = Math.floor(value / 60);
        return `${`00${minutes}`.slice(-2)}:${`00${Math.floor(value - minutes * 60)}`.slice(-2)}`;
    }

    ngOnDestroy() {
        this.subscriber.unsubscribe();
        if (this.serverInfo?.notification && this.notification()) {
            this.bottomSheet.dismiss();
        }
        if (this.countDown) {
            this.countDown.unsubscribe();
            this.countDown = null;
        }
    }
}
